import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Sparkles, Bell, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Sparkle, Heart, Star, Flower, Cupcake, Gift } from "../doodles/Doodles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable Coming Soon page with countdown + email notify list.
 * Props:
 *   pageId      — string ("occasions" | "table-grazing" | ...)   for notify segmentation
 *   pageTestId  — root data-testid, e.g. "shop-occasions" or "table-grazing-page"
 *   stampLabel  — the small pill label at the top (default "Coming Soon")
 *   titleLine1  — big doodle text — line 1
 *   titleLine2  — big doodle text — line 2 (accent-coloured)
 *   description — main paragraph
 *   subCopy     — smaller supporting paragraph
 *   launchDateISO — ISO string, e.g. "2026-10-01T00:00:00+05:30" — powers the countdown
 *   accentColor — hex accent used for the second title line
 */
export default function ComingSoonPage({
  pageId,
  pageTestId,
  stampLabel = "Coming Soon",
  titleLine1,
  titleLine2,
  description,
  subCopy,
  launchDateISO,
  accentColor = "#E08036",
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Tick the countdown every second
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Remember on this device if the user already signed up
  useEffect(() => {
    const key = `nc_notify_signed_${pageId}`;
    if (localStorage.getItem(key) === "1") setSignedUp(true);
  }, [pageId]);

  const target = launchDateISO ? new Date(launchDateISO).getTime() : null;
  const diff = target ? Math.max(0, target - now) : 0;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  const launched = target && diff === 0;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/notify-signup`, { email: trimmed, page: pageId });
      localStorage.setItem(`nc_notify_signed_${pageId}`, "1");
      setSignedUp(true);
      setEmail("");
      toast.success(res.data?.already_signed_up ? "You're already on the list! 💌" : "You're on the list — see you soon 💌");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const parts = [
    { label: "days", value: days },
    { label: "hours", value: hours },
    { label: "mins", value: minutes },
    { label: "secs", value: seconds },
  ];

  return (
    <div className="pt-32 pb-20 min-h-[80vh] relative overflow-hidden" data-testid={pageTestId}>
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #FFE0B5 0%, transparent 50%), radial-gradient(circle at 70% 70%, #FFD4DC 0%, transparent 50%), linear-gradient(135deg, #FFF7E6 0%, #FFE7F1 100%)",
        }}
      />

      <Flower color="#FFA94D" className="absolute top-28 left-8 w-16 sm:w-24 rotate-12 opacity-90"/>
      <Flower color="#FF6B9D" className="absolute bottom-16 right-10 w-16 sm:w-24 -rotate-12 opacity-90"/>
      <Sparkle color="#FFD93D" className="absolute top-40 right-20 w-6 animate-twinkle"/>
      <Sparkle color="#E08036" className="absolute bottom-32 left-1/3 w-5 animate-twinkle" style={{ animationDelay: "0.6s" }}/>
      <Heart color="#FF6B9D" className="absolute top-1/2 left-12 w-6 animate-bounce-doodle hidden sm:block"/>
      <Star color="#BAE1FF" className="absolute top-44 left-1/3 w-6 animate-spin-slow hidden sm:block"/>
      <Cupcake className="absolute bottom-24 left-1/4 w-14 animate-float-slow hidden md:block"/>
      <Gift color="#DDC5FF" className="absolute top-1/2 right-1/4 w-14 animate-float-fast hidden md:block"/>

      <div className="container-x relative text-center">
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border-2 border-[#7A1F2B] font-hand text-xs uppercase tracking-widest text-[#7A1F2B] -rotate-2 rounded-full mb-3"
          style={{ boxShadow: "3px 3px 0 #7A1F2B" }}
          data-testid={`${pageId}-coming-soon-stamp`}
        >
          <Sparkles className="w-3 h-3"/> {launched ? "We're live!" : stampLabel}
        </span>

        <h1 className="font-doodle text-4xl sm:text-5xl lg:text-6xl text-[#7A1F2B] leading-tight">
          {titleLine1} <br/>
          <span style={{ color: accentColor }}>{titleLine2}</span>
        </h1>

        {description && (
          <p className="mt-5 font-hand text-base sm:text-lg text-[#2D2424] max-w-2xl mx-auto italic leading-relaxed">
            {description}
          </p>
        )}

        {/* Countdown */}
        {target && !launched && (
          <div className="mt-8 flex justify-center gap-2 sm:gap-3 flex-wrap" data-testid={`${pageId}-countdown`}>
            {parts.map((p) => (
              <div
                key={p.label}
                className="bg-white border-2 border-[#2D2424] rounded-2xl px-3 sm:px-5 py-2 sm:py-3 -rotate-2 hover:rotate-0 transition-transform min-w-[68px] sm:min-w-[84px]"
                style={{ boxShadow: "4px 4px 0 #7A1F2B" }}
                data-testid={`${pageId}-countdown-${p.label}`}
              >
                <p className="font-doodle text-3xl sm:text-4xl text-[#7A1F2B] leading-none tabular-nums">
                  {String(p.value).padStart(2, "0")}
                </p>
                <p className="font-hand text-[10px] sm:text-xs text-[#5C5555] uppercase tracking-widest mt-1">
                  {p.label}
                </p>
              </div>
            ))}
          </div>
        )}

        {subCopy && (
          <p className="mt-6 font-hand text-sm text-[#5C5555] max-w-xl mx-auto">
            {subCopy}
          </p>
        )}

        {/* Notify list */}
        <div className="mt-8 max-w-md mx-auto" data-testid={`${pageId}-notify-block`}>
          {signedUp ? (
            <div
              className="inline-flex items-center gap-2 px-4 py-3 bg-[#EAFBE7] border-2 border-[#2D2424] rounded-full font-hand text-sm text-[#2D2424]"
              style={{ boxShadow: "3px 3px 0 #2D2424" }}
              data-testid={`${pageId}-notify-success`}
            >
              <CheckCircle2 className="w-5 h-5 text-green-700"/> You&apos;re on the list — we&apos;ll email you the moment we launch 💌
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 items-stretch">
              <div className="relative flex-1">
                <Bell className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A1F2B]"/>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  required
                  data-testid={`${pageId}-notify-input`}
                  className="w-full pl-9 pr-3 py-3 border-2 border-[#2D2424] rounded-full bg-white font-hand text-sm text-[#2D2424] placeholder-[#9B9494] focus:outline-none focus:border-[#7A1F2B]"
                  style={{ boxShadow: "3px 3px 0 #2D2424" }}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid={`${pageId}-notify-submit`}
                className="btn-primary shrink-0"
              >
                <Sparkles className="w-4 h-4"/> {submitting ? "Adding…" : "Notify me"}
              </button>
            </form>
          )}
          <p className="mt-2 font-hand text-[11px] text-[#9B9494]">
            No spam — just one gentle email the day it goes live.
          </p>
        </div>

        <div className="mt-10 inline-flex items-end gap-2" data-testid={`${pageId}-visual`}>
          <Gift color="#FFD93D" className="w-12 animate-float-slow"/>
          <Heart color="#FF6B9D" className="w-14 animate-float-fast"/>
          <Cupcake className="w-16 animate-float-slow" style={{ animationDelay: "0.5s" }}/>
          <Star color="#BAE1FF" className="w-12 animate-float-fast" style={{ animationDelay: "1s" }}/>
          <Gift color="#DDC5FF" className="w-14 animate-float-slow" style={{ animationDelay: "1.5s" }}/>
        </div>

        <div className="mt-8">
          <Link to="/" data-testid={`${pageId}-back-btn`} className="btn-secondary">
            <ArrowLeft className="w-4 h-4"/> Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
